import type { RouterBuildResponse, RouterExecutionData } from './types'

// M4 audit finding 3 (High): the client must not turn an arbitrary /v1/build JSON body into a
// wallet transaction without binding it back to the quote the user actually accepted. Every check
// below is fail-closed — an unrecognized/missing/malformed field is treated as a rejection, never
// as "probably fine". Keep this file's constants in sync with the registered Robinhood deployment.
export const SUPPORTED_BUILD_SCHEMA_VERSIONS = new Set(['1.1.0'])
export const ROBINHOOD_CHAIN_ID = 4663
export const ROBINHOOD_UNIVERSAL_ROUTER = '0x8876789976dEcBfCbBbe364623C63652db8C0904'
export const ROBINHOOD_PERMIT2 = '0x000000000022D473030F116dDEE9F6B43aC78BA3'
export const UR_EXECUTE_SELECTOR = '0x3593564c'
export const NATIVE_SENTINEL = '0x0000000000000000000000000000000000000000'

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const NONNEG_INT_RE = /^[0-9]+$/

function isAddress(v: unknown): v is string {
  return typeof v === 'string' && ADDRESS_RE.test(v)
}

function sameAddress(a: unknown, b: unknown): boolean {
  return isAddress(a) && isAddress(b) && (a as string).toLowerCase() === (b as string).toLowerCase()
}

/** feeRecipient is `string | null`; null is only equal to null, never to an address. */
function sameNullableAddress(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return a === b || (a == null && b == null)
  return sameAddress(a, b)
}

function isNonNegativeIntString(v: unknown): v is string {
  return typeof v === 'string' && NONNEG_INT_RE.test(v)
}

export type BuildValidationResult = { ok: true } | { ok: false; reason: string }

/**
 * Binds a `/v1/build` response to the `RouterExecutionData` the user actually accepted at quote
 * time (plus, optionally, the user's chosen slippage) and fails closed on any mismatch. The caller
 * (robinhoodRouterBuild.ts) must throw before `sendTransaction()` when `ok` is false.
 */
export function validateBuildResponse(
  build: RouterBuildResponse,
  accepted: RouterExecutionData,
  slippageBps: number
): BuildValidationResult {
  const fail = (reason: string): BuildValidationResult => ({ ok: false, reason })

  if (!build || typeof build !== 'object') return fail('build response is not an object')

  if (!SUPPORTED_BUILD_SCHEMA_VERSIONS.has(build.schemaVersion)) {
    return fail(`unsupported schemaVersion: ${String(build.schemaVersion)}`)
  }

  if (build.buildable !== true || build.simulation?.success !== true) {
    return fail('build is not buildable or simulation did not succeed')
  }

  if (build.chainId !== ROBINHOOD_CHAIN_ID) {
    return fail(`chainId mismatch: expected ${ROBINHOOD_CHAIN_ID}, got ${String(build.chainId)}`)
  }

  if (build.routeId !== accepted.routeId) {
    return fail('routeId does not match the accepted quote')
  }

  if (build.deploymentVersion !== accepted.deploymentVersion) {
    return fail('deploymentVersion does not match the accepted quote')
  }

  if (!sameAddress(build.tokenIn, accepted.tokenIn)) {
    return fail('tokenIn does not match the accepted quote')
  }
  if (!sameAddress(build.tokenOut, accepted.tokenOut)) {
    return fail('tokenOut does not match the accepted quote')
  }
  if (!isNonNegativeIntString(build.amountIn) || build.amountIn !== accepted.amountIn) {
    return fail('amountIn does not match the accepted quote')
  }

  if (typeof build.fee?.feeBps !== 'number' || build.fee.feeBps !== accepted.fee.feeBps) {
    return fail('fee.feeBps does not match the accepted quote')
  }
  if (!sameAddress(build.fee?.feeToken, accepted.fee.feeToken)) {
    return fail('fee.feeToken does not match the accepted quote')
  }
  if (!sameNullableAddress(build.fee?.feeRecipient, accepted.fee.feeRecipient)) {
    return fail('fee.feeRecipient does not match the accepted quote')
  }

  const tx = build.transaction
  if (!tx || !sameAddress(tx.to, ROBINHOOD_UNIVERSAL_ROUTER)) {
    return fail('transaction.to is not the registered Robinhood Universal Router')
  }
  if (typeof tx.data !== 'string' || !tx.data.toLowerCase().startsWith(UR_EXECUTE_SELECTOR)) {
    return fail('transaction.data does not start with the execute() selector')
  }

  const isNativeInput = isAddress(accepted.tokenIn) && accepted.tokenIn.toLowerCase() === NATIVE_SENTINEL

  if (!isNonNegativeIntString(tx.value)) {
    return fail('transaction.value is not a valid nonnegative integer string')
  }
  if (isNativeInput) {
    if (tx.value !== accepted.amountIn) return fail('transaction.value does not equal amountIn for native input')
  } else {
    if (tx.value !== '0') return fail('transaction.value must be "0" for ERC-20 input')
  }

  if (!isNonNegativeIntString(tx.gasLimit)) return fail('transaction.gasLimit is not a valid nonnegative integer string')
  if (!isNonNegativeIntString(build.freshNetAmountOut)) return fail('freshNetAmountOut is not a valid nonnegative integer string')
  if (!isNonNegativeIntString(build.minimumNetAmountOut)) return fail('minimumNetAmountOut is not a valid nonnegative integer string')
  if (!isNonNegativeIntString(build.feeAmount)) return fail('feeAmount is not a valid nonnegative integer string')

  if (isNativeInput) {
    if (build.approval?.type !== 'NONE') return fail('approval.type must be NONE for native input')
  } else {
    if (build.approval?.type !== 'PERMIT2') return fail('approval.type must be PERMIT2 for ERC-20 input')
    if (!sameAddress(build.approval.erc20AllowanceTarget, ROBINHOOD_PERMIT2)) {
      return fail('approval.erc20AllowanceTarget is not the registered Permit2 contract')
    }
    if (!sameAddress(build.approval.permit2Spender, ROBINHOOD_UNIVERSAL_ROUTER)) {
      return fail('approval.permit2Spender is not the registered Robinhood Universal Router')
    }
  }

  // slippageBps is required (not optional) so this floor check can never be silently skipped by a
  // caller that forgets to pass it — it's the one check preventing a build from returning less than
  // the user accepted.
  if (!isNonNegativeIntString(accepted.netAmountOut)) {
    return fail('accepted netAmountOut is not a valid nonnegative integer string')
  }
  if (slippageBps < 0 || slippageBps > 10_000) {
    return fail('slippageBps is out of range')
  }
  const floor = (BigInt(accepted.netAmountOut) * BigInt(10_000 - slippageBps)) / 10_000n
  if (BigInt(build.minimumNetAmountOut) < floor) {
    return fail('minimumNetAmountOut is weaker than the accepted quote floor for the requested slippage')
  }

  return { ok: true }
}
